import { MAZE } from '@somnhold/content';
import { DIRS, hexLen, hexRing, type Hex } from './hex.ts';
import { idiv, imax, mulBp } from './math/fixed.ts';
import { deriveSeed, rngInt, seedRng, type RngState } from './math/rng.ts';
import type { DungeonRoom, MapDef, MazeEdge, MazeLayout } from './types.ts';

/*
 * The Furrows (docs/LABERINTOS.md): a labyrinth laid out from the week's seed. Crossings sit on a coarse hex grid
 * (coarse (Q, R) is map cell (2Q, 2R)), so two neighbouring crossings have one corridor cell between them. The
 * middle seven crossings make the hall. A depth-first walk from a crossing on the edge carves a perfect maze; a few
 * extra corridors make loops, some shut by gates (their lever on the deeper side) and some that breathe.
 */

const shuffle = <T>(rng: RngState, a: T[]): T[] => {
  for (let i = a.length - 1; i > 0; i--) {
    const j = rngInt(rng, i + 1);
    const t = a[i] as T;
    a[i] = a[j] as T;
    a[j] = t;
  }
  return a;
};

/** Breadth-first corridor steps from `from`, over the given edges. */
const stepsFrom = (n: number, edges: { a: number; b: number }[], from: number): number[] => {
  const adj: number[][] = Array.from({ length: n }, () => []);
  for (const e of edges) {
    adj[e.a]?.push(e.b);
    adj[e.b]?.push(e.a);
  }
  const d = new Array<number>(n).fill(-1);
  d[from] = 0;
  const q = [from];
  for (let i = 0; i < q.length; i++) {
    const a = q[i] as number;
    for (const b of adj[a] as number[]) {
      if (d[b] !== -1) continue;
      d[b] = (d[a] as number) + 1;
      q.push(b);
    }
  }
  return d;
};

const layout = (seed: number, R: number): MazeLayout | null => {
  const rng = seedRng(seed ^ 0x3a2e);
  // Crossings outside the hall, row by row; the hall last.
  const coarse: Hex[] = [];
  for (let r = -R; r <= R; r++) for (let q = -R; q <= R; q++) if (hexLen(q, r) <= R && hexLen(q, r) >= 2) coarse.push({ q, r });
  const index = new Map<string, number>();
  coarse.forEach((c, i) => index.set(`${c.q},${c.r}`, i));
  const hall = coarse.length;
  const nodes: Hex[] = [...coarse.map((c) => ({ q: c.q * 2, r: c.r * 2 })), { q: 0, r: 0 }];
  const n = nodes.length;
  // Every possible corridor, by crossing pair (a < b). Into the hall: from a crossing two out, through the cell
  // between it and the inner crossing it faces.
  const all: { a: number; b: number; q: number; r: number }[] = [];
  coarse.forEach((c, a) => {
    for (const d of DIRS) {
      const o = { q: c.q + d.q, r: c.r + d.r };
      const len = hexLen(o.q, o.r);
      if (len > R) continue;
      const cell = { q: c.q * 2 + d.q, r: c.r * 2 + d.r };
      if (len <= 1) {
        if (!all.some((e) => e.a === a && e.b === hall)) all.push({ a, b: hall, ...cell });
        continue;
      }
      const b = index.get(`${o.q},${o.r}`) as number;
      if (a < b) all.push({ a, b, ...cell });
    }
  });
  // The way in: a crossing on the rim.
  const rim = coarse.map((c, i) => (hexLen(c.q, c.r) === R ? i : -1)).filter((i) => i >= 0);
  const entry = rim[rngInt(rng, rim.length)] as number;
  // Carve the tree outside the hall.
  const adj: { b: number; e: number }[][] = Array.from({ length: n }, () => []);
  all.forEach((e, i) => {
    if (e.b === hall) return;
    adj[e.a]?.push({ b: e.b, e: i });
    adj[e.b]?.push({ b: e.a, e: i });
  });
  const inTree = new Array<boolean>(all.length).fill(false);
  const visited = new Array<boolean>(n).fill(false);
  visited[entry] = true;
  const stack = [entry];
  while (stack.length) {
    const a = stack[stack.length - 1] as number;
    const next = shuffle(rng, (adj[a] as { b: number; e: number }[]).filter((x) => !visited[x.b]));
    if (!next.length) {
      stack.pop();
      continue;
    }
    const pick = next[0] as { b: number; e: number };
    inTree[pick.e] = true;
    visited[pick.b] = true;
    stack.push(pick.b);
  }
  // Two ways into the hall: the open one from the crossing deepest in the tree (the long way round), and a gate on
  // the far side whose lever is in the hall (a shortcut once the centre has been reached).
  const intoHall = all.map((e, i) => (e.b === hall ? i : -1)).filter((i) => i >= 0);
  const treeDepth = stepsFrom(
    n,
    all.filter((_, i) => inTree[i]),
    entry,
  );
  // The open way in leaves from about two thirds of the tree's depth: a long way, not the farthest corner.
  let deepest = 0;
  for (const d of treeDepth) deepest = imax(deepest, d);
  const aim = mulBp(deepest, MAZE.hallDepthBp);
  let first = intoHall[0] as number;
  const off = (i: number): number => Math.abs((treeDepth[all[i]?.a as number] as number) - aim);
  for (const i of intoHall) if (off(i) < off(first)) first = i;
  inTree[first] = true;
  const f = nodes[all[first]?.a as number] as Hex;
  let second = first;
  let far = -1;
  for (const i of intoHall) {
    const o = nodes[all[i]?.a as number] as Hex;
    const d = hexLen(o.q - f.q, o.r - f.r);
    if (d > far) {
      far = d;
      second = i;
    }
  }
  const tree = all.filter((_, i) => inTree[i]);
  const depth = stepsFrom(n, tree, entry);
  // Loops: extra corridors, the ones joining far parts of the tree first make the best shortcuts.
  const extras = shuffle(
    rng,
    all.map((_, i) => i).filter((i) => !inTree[i] && i !== second && (all[i] as { b: number }).b !== hall),
  ).slice(0, imax(MAZE.gates, mulBp(n, MAZE.loopsBp)));
  const gap = (i: number): number => Math.abs((depth[all[i]?.a as number] as number) - (depth[all[i]?.b as number] as number));
  const byGap = extras.slice().sort((x, y) => gap(y) - gap(x) || x - y);
  const gates = new Set(byGap.slice(0, MAZE.gates));
  const edges: MazeEdge[] = [];
  const kept = all.map((_, i) => i).filter((i) => inTree[i] || i === second || extras.includes(i));
  if (second !== first) gates.add(second);
  for (const i of kept) {
    const e = all[i] as { a: number; b: number; q: number; r: number };
    edges.push({ a: e.a, b: e.b, q: e.q, r: e.r, kind: gates.has(i) ? 'gate' : 'path', phase: 0 });
  }
  // Breathing walls: away from the way in and the hall, any corridor but a gate.
  const touches = (e: MazeEdge, x: number): boolean => e.a === x || e.b === x;
  const breathable = shuffle(
    rng,
    edges.map((e, i) => (e.kind === 'path' && !touches(e, entry) && !touches(e, hall) ? i : -1)).filter((i) => i >= 0),
  ).slice(0, MAZE.breaths);
  const cycle = MAZE.breathOpen + MAZE.breathShut;
  for (const i of breathable.sort((x, y) => x - y)) {
    const e = edges[i] as MazeEdge;
    e.kind = 'breath';
    e.phase = rngInt(rng, cycle);
  }
  const levers = edges
    .map((e, i) => ({ e, i }))
    .filter(({ e }) => e.kind === 'gate')
    .map(({ e, i }) => ({ edge: i, node: e.b === hall ? hall : (depth[e.a] as number) >= (depth[e.b] as number) ? e.a : e.b }));
  // The way to the hall (by the tree), for the camps.
  const parent = new Array<number>(n).fill(-1);
  {
    const q = [entry];
    const seen = new Array<boolean>(n).fill(false);
    seen[entry] = true;
    for (let k = 0; k < q.length; k++) {
      const a = q[k] as number;
      for (const e of tree) {
        const b = e.a === a ? e.b : e.b === a ? e.a : -1;
        if (b < 0 || seen[b]) continue;
        seen[b] = true;
        parent[b] = a;
        q.push(b);
      }
    }
  }
  const way: number[] = [];
  for (let x = all[first]?.a as number; x >= 0; x = parent[x] as number) way.unshift(x);
  if (way.length < 4) return null;
  const camps = MAZE.camps.map((bp) => way[imax(1, idiv((way.length - 1) * bp, 10000))] as number);
  // Chests in the dead ends farthest from the way in.
  const degree = new Array<number>(n).fill(0);
  for (const e of edges) {
    degree[e.a] = (degree[e.a] as number) + 1;
    degree[e.b] = (degree[e.b] as number) + 1;
  }
  const leverAt = new Set(levers.map((l) => l.node));
  const ends = nodes
    .map((_, i) => i)
    .filter((i) => degree[i] === 1 && i !== entry && i !== hall && !camps.includes(i) && !leverAt.has(i))
    .sort((x, y) => (depth[y] as number) - (depth[x] as number) || x - y);
  if (ends.length < MAZE.chests) return null;
  const chests = ends.slice(0, MAZE.chests);
  const guarded = chests.map((_, i) => i % MAZE.guardEvery === MAZE.guardEvery - 1);
  const free = shuffle(
    rng,
    nodes.map((_, i) => i).filter((i) => i !== hall && (depth[i] as number) >= MAZE.packMinDepth && !camps.includes(i) && !chests.includes(i)),
  );
  // Something on the way: a pack every few crossings along the way in to the hall, then the rest anywhere.
  const onWay = way.filter((x) => (depth[x] as number) >= MAZE.packMinDepth && ((depth[x] as number) - MAZE.packMinDepth) % MAZE.wayPackEvery === 0 && !camps.includes(x));
  const packs = [...new Set([...onWay, ...free.filter((x) => !onWay.includes(x)).slice(0, MAZE.packs)])].sort((x, y) => x - y);
  const doors = edges.map((e, i) => (e.kind === 'gate' ? i : -1)).filter((i) => i >= 0);
  for (let i = 0; i < edges.length; i++) if ((edges[i] as MazeEdge).kind === 'breath') doors.push(i);
  return { nodes, edges, entry, hall, depth, camps, chests, guarded, packs, levers, doors };
};

/** Every crossing reachable from the way in with the gates shut and the breathing walls open. */
const sound = (L: MazeLayout): boolean => {
  const d = stepsFrom(
    L.nodes.length,
    L.edges.filter((e) => e.kind !== 'gate'),
    L.entry,
  );
  return d.every((x) => x >= 0);
};

/**
 * The week's labyrinth and its map: crossings, corridors and the hall are the floor; gates and breathing walls are
 * the doors. Rooms (for light and the way in): the way in, the camps and the hall.
 */
export const createMaze = (seed: number, radius: number = MAZE.radius): { layout: MazeLayout; map: MapDef } => {
  let L: MazeLayout | null = null;
  for (let k = 0; k < 16 && !L; k++) {
    const t = layout(k === 0 ? seed : deriveSeed(seed, k), radius);
    if (t && sound(t)) L = t;
  }
  if (!L) throw new Error('maze: no sound layout');
  const floor = new Map<string, Hex>();
  const add = (h: Hex): void => {
    floor.set(`${h.q},${h.r}`, { q: h.q, r: h.r });
  };
  L.nodes.forEach((h, i) => {
    if (i !== L.hall) add(h);
  });
  add({ q: 0, r: 0 });
  for (let k = 1; k <= 2; k++) for (const h of hexRing(k)) add(h);
  for (const e of L.edges) add(e);
  const cells = [...floor.values()].sort((x, y) => x.r - y.r || x.q - y.q);
  let maxD = 0;
  for (const h of cells) maxD = imax(maxD, hexLen(h.q, h.r));
  const room = (i: number, kind: DungeonRoom['kind'], radius = 0): DungeonRoom => ({ q: (L.nodes[i] as Hex).q, r: (L.nodes[i] as Hex).r, radius, kind });
  const rooms: DungeonRoom[] = [room(L.entry, 'entry'), ...L.camps.map((c) => room(c, 'room')), room(L.hall, 'boss', 2)];
  const doors = L.doors.map((i) => ({ q: (L.edges[i] as MazeEdge).q, r: (L.edges[i] as MazeEdge).r }));
  return { layout: L, map: { radius: maxD + 2, bastion: 0, sectors: [], kind: 'dungeon', look: 'furrows', dungeon: { rooms, doors, floor: cells } } };
};
